/* eslint-disable no-unused-vars */
import * as Colyseus from "colyseus.js";
import NoSleep from 'nosleep.js';

import LoggingService from "services/logging";
import { audio } from "services/audioManager";
import { APP_TYPES } from 'constants/AppTypes';
import { getBaseUrl } from 'helpers/url';

//components
import React, { Component } from 'react';
import { Route } from 'react-router';
import QRCode from 'qrcode.react';
import Loading from "components/Loading";
import Menu from "components/Menu";
import Player from "components/Player";
import ReactPlayer from 'react-player'
import MoneyDrop from "components/hostSubComponents/MoneyDrop";
import RoomDetails from "components/hostSubComponents/RoomDetails";
import RoundIntro from "components/hostSubComponents/RoundIntro";
import GameConfigView from "components/hostSubComponents/GameConfigView";
import TeamsSelection from "components/hostSubComponents/TeamsSelection";
import RoundSummary from "components/hostSubComponents/RoundSummary";
import WinnerSection from "components/hostSubComponents/WinnerSection";
import PlayAgainSection from "components/hostSubComponents/PlayAgainSection";

//styles
import "animate.css";
import styles from 'components/HostStyles.module.scss';

//images
import logo from "images/logo.png";
import BonusBoard from "images/BonusBoard.png";
import fullscreenIcon from "images/Host/lobby_fullscreen.png";
import helpIcon from "images/Host/lobby_help.png";
import Trophy from "images/Trophy.png";
import CatchphraseScreen from "images/CatchphraseScreen.png";
import BG from "images/BG.png";
import studioBG from "images/HostBG.jpg";

//audio constants
import { MUSIC, SFX, VO, pickVariant, flattenVO } from "constants/audio";

//constants
import { GameStates, CatchphraseServerOutMessages, CatchphraseServerInMessages } from "constants/constants"
import { testRoomState } from "constants/testData";
import VideoPlayer from "./VideoPlayer/VideoPlayer";

const fullscreenAvailable = document.fullscreenEnabled || document.mozFullscreenEnabled || document.webkitFullscreenEnabled ? true : false;

const gameId = "catchphrase";

export class Home extends Component {
    static displayName = Home.name;

    constructor(props) {
        super(props);

        this.client = new Colyseus.Client(process.env.REACT_APP_GAME_SERVER_URL);
        this.state = {
            roomId: 0,
            room: null,
            myId: null,
            roomState: {},
            //roomState: testRoomState, //Debug
            redirect: null,
            reconnectTries: 0,
            connected: false,
            //connected: true, //Debug
            menuOpen: false,
            tickedSkipTutorial: true,

            players: [],
            playersRequired: 2,
            showPlayers: false,

            doingTutorial: false,
            showTutorial: false,
            skipTutorialCount: 0,
            showStartButtons: false,
            showPlayAgainButtons: false,
            showGameConfig: false,
            showTeamsSection: false,
            hideVideo: true,

            videoReady: false,
            showCatchphrase: false,

            gotLocationPing: true,
            connectionIssue: false,
            isMirror: false,
            flashingSegment: null,
            platform: null,
        };

        this.toggleMenu = this.toggleMenu.bind(this);
        // create ref for the video player
        this.videoPlayer = React.createRef();
        //this.sleepController = new AbortController();
    }

    sleep(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    /** Send a message only if this is the primary host (not a mirror) */
    sendIfPrimary = (message, data = {}) => {
        if (this.state.isMirror) return;
        this.state.room?.send(message, data);
    }

    get isPrimary() { return !this.state.isMirror; }

    componentDidMount() {
        this.setTags();
        audio.init();
        audio.prime([...Object.values(MUSIC), ...Object.values(SFX), ...flattenVO(VO)]);
        audio.playMusic(MUSIC.MENU, { volume: 0.15 });
        this.doReconnect();
        //this.toggleMute(true, false);

        // Add network status listeners
        window.addEventListener('offline', this.handleOffline);
        window.addEventListener('online', this.handleOnline);

        // Initialize NoSleep to prevent screen from sleeping
        try {
            this.noSleep = new NoSleep();
            // TV remotes mostly fire keydown; only OK reliably fires click.
            // NoSleep.enable() is idempotent so multiple triggers are safe.
            ['click', 'touchstart', 'keydown', 'keyup'].forEach(ev => {
                document.addEventListener(ev, this.enableNoSleep, { once: true });
            });
            // Re-trigger periodically in case the dummy video gets paused by the OS.
            this.noSleepInterval = setInterval(() => this.enableNoSleep(), 60000);
        } catch (e) {
            console.warn('[Host] NoSleep initialization failed:', e);
        }

        // Wake Lock auto-releases when the page is hidden; reacquire on visibility change.
        document.addEventListener('visibilitychange', this.handleVisibilityChange);

        //Dev only
        if (window.location.hostname === "localhost") {
            setInterval(() => {
                var iframes = document.querySelectorAll('iframe');
                if (iframes.length > 0) console.log("Developer: ", "react iframe found, trying to remove it");
                for (var i = 0; i < iframes.length; i++) {
                    iframes[i].parentNode.removeChild(iframes[i]);
                }
            }, 1000);
        }

        // Beacon API: signal server when host closes tab/navigates away
        this.handleBeforeUnload = () => {
            if (this.state.isMirror) return; // Mirrors don't send host-leaving beacons
            const roomCode = this.state.roomState?.roomCode || this.getRoomCodeFromURL();
            if (roomCode) {
                const serverUrl = this.getServerHttpUrl();
                navigator.sendBeacon(`${serverUrl}/api/host-leaving/${roomCode}`, '');
            }
        };
        window.addEventListener('beforeunload', this.handleBeforeUnload);

        // Detect if this is a refresh — cancel pending closure timer on server
        const roomCode = this.getQueryStringValue('code');
        const navType = performance.getEntriesByType?.('navigation')?.[0]?.type;
        if (roomCode && navType === 'reload') {
            console.log('[Host] Refresh detected, cancelling closure timer for:', roomCode);
            fetch(`${this.getServerHttpUrl()}/api/host-refresh/${roomCode}`, {
                method: 'POST',
                body: '',
            }).catch(e => console.warn('[Host] Failed to cancel closure timer:', e));
        }
    }

    setTags() {
        // Tags for debugging (Sentry removed)
    }

    // Persist config changes the moment they happen, not only on Start. The host's
    // localStorage tracks the *current* schema values during GameConfig so a user who
    // picks Short/Solo/Say It then backs out without confirming still gets those as
    // defaults next time. Outside GameConfig the fields don't change, so this is a
    // no-op the rest of the time.
    //
    // Why snapshot strings, not prev vs curr field comparison: Colyseus mutates the same
    // schema object in place and React's setState gets the same reference each time, so
    // `prevState.roomState.catchphraseData === this.state.roomState.catchphraseData` is
    // always true and direct field comparisons read the same memory both sides.
    // _lastConfigSnapshot is captured at the first GameConfig observation so the initial
    // schema state (or a restore round-trip) doesn't immediately get re-saved on top of
    // itself.
    componentDidUpdate() {
        if (this.state.isMirror) return;
        const curr = this.state.roomState?.catchphraseData;
        if (!curr || curr.gameState !== GameStates.GameConfig) {
            this._lastConfigSnapshot = null;
            return;
        }
        const snapshot = `${curr.gameType}|${curr.gameMode}|${curr.answerMode}`;
        if (this._lastConfigSnapshot == null) {
            this._lastConfigSnapshot = snapshot;
            return;
        }
        if (snapshot !== this._lastConfigSnapshot) {
            this._lastConfigSnapshot = snapshot;
            this.saveCurrentConfig();
        }
    }

    componentWillUnmount() {
        window.removeEventListener('beforeunload', this.handleBeforeUnload);
        window.removeEventListener('offline', this.handleOffline);
        window.removeEventListener('online', this.handleOnline);
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);

        // Clean up NoSleep
        if (this.noSleep) {
            try {
                this.noSleep.disable();
            } catch (e) {
                console.warn('[Host] NoSleep disable failed:', e);
            }
        }
        if (this.noSleepInterval) {
            clearInterval(this.noSleepInterval);
            this.noSleepInterval = null;
        }
        if (this.wakeLockSentinel) {
            this.wakeLockSentinel.release().catch(() => {});
            this.wakeLockSentinel = null;
        }

        // Clear location check interval
        if (this.locationCheckInterval) {
            clearInterval(this.locationCheckInterval);
        }

        audio.destroy();
    }


    enableNoSleep = () => {
        if (this.noSleep) {
            this.noSleep.enable().catch(e => console.warn('[Host] NoSleep enable failed:', e));
        }
        this.requestWakeLock();
    };

    requestWakeLock = async () => {
        if (!('wakeLock' in navigator) || this.wakeLockSentinel) return;
        try {
            this.wakeLockSentinel = await navigator.wakeLock.request('screen');
            this.wakeLockSentinel.addEventListener('release', () => {
                this.wakeLockSentinel = null;
            });
        } catch (e) {
            // Common: no user gesture yet, page not visible. Safe to ignore.
        }
    };

    handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
            this.enableNoSleep();
        }
    };

    handleOffline = () => {
        console.log('[Host] Network offline detected');
        this.setState({ connectionIssue: true });
    };

    handleOnline = () => {
        console.log('[Host] Network online detected');
        this.setState({ connectionIssue: false });
        // Attempt to reconnect if not already connected
        if (!this.state.connected) {
            console.log('[Host] Network back online, attempting reconnect');
            this.doReconnect();
        }
    };

    toggleFullScreen = () => {
        audio.playSfx(SFX.BUTTON_PRESS);
        if (fullscreenAvailable) {
            if (!document.fullscreenElement && !document.webkitIsFullScreen && !document.mozFullScreen && !document.msFullscreenElement) {
                let elem = document.documentElement
                if (elem.requestFullscreen) {
                    elem.requestFullscreen();
                } else if (elem.webkitRequestFullscreen) {
                    elem.webkitRequestFullscreen();
                } else if (elem.mozRequestFullScreen) {
                    elem.mozRequestFullScreen();
                } else if (elem.msRequestFullscreen) {
                    elem.msRequestFullscreen();
                }
            } else {
                if (document.exitFullscreen) {
                    document.exitFullscreen();
                } else if (document.webkitExitFullscreen) {
                    document.webkitExitFullscreen();
                } else if (document.mozExitFullscreen) {
                    document.mozExitFullscreen();
                } else if (document.msExitFullscreen) {
                    document.msExitFullscreen();
                }
            }
        }
    }

    getQueryStringValue(key) {
        return decodeURIComponent(window.location.search.replace(new RegExp("^(?:.*[&\\?]" + encodeURIComponent(key).replace(/[.+*]/g, "\\$&") + "(?:\\=([^&]*))?)?.*$", "i"), "$1"));
    }

    animatePotato(id, animation) {
        if (document.getElementById(`potato-${id}`)) {
            this.animateCSS(`#potato-${id}`, animation);
        }
    }

    animateCSS = (element, animation, prefix = 'animate__') =>
        // We create a Promise and return it
        new Promise((resolve, reject) => {
            const animationName = `${prefix}${animation}`;
            let node;
            if (typeof element === `string`) {
                node = document.querySelector(element);
            } else {
                node = element;
            }
            node.classList.add(`${prefix}animated`, animationName);

            // When the animation ends, we clean the classes and resolve the Promise
            function handleAnimationEnd(event) {
                event.stopPropagation();
                node.classList.remove(`${prefix}animated`, animationName);
                resolve('Animation ended');
            }

            node.addEventListener('animationend', handleAnimationEnd, { once: true });
        });

    toggleMenu() {
        audio.playSfx(SFX.BUTTON_PRESS);
        console.log("toggle menu");
        let newVal = this.state.menuOpen === true ? false : true;
        this.setState({ menuOpen: newVal });
    }

    toggleMute = () => {
        const catchphraseData = this.state.roomState?.catchphraseData;
        const bothMuted = catchphraseData?.muteMusic && catchphraseData?.muteSfx;
        // If either is unmuted, mute both. If both muted, unmute both.
        const shouldMute = !bothMuted;
        this.state.room.send("mute_music", { mute: shouldMute });
        this.state.room.send("mute_sfx", { mute: shouldMute });
    }

    signalStartGame = async () => {
        if (this.state.isMirror) return;
        audio.playSfx(SFX.BUTTON_PRESS);
        this.setState({ showStartButtons: false });
        this.state.room.send("begin_game", { skipTutorial: this.state.tickedSkipTutorial });

    }

    signalGoToLobby = () => {
        if (this.state.isMirror) return;
        audio.playSfx(SFX.BUTTON_PRESS);
        this.state.room.send("change_game", {});
    }

    signalConfirmGameConfig = async () => {
        if (this.state.isMirror) return;
        audio.stopAllVo();
        audio.playSfx(SFX.BUTTON_PRESS);
        this.setState({ showGameConfig: false });
        await this.sleep(500);
        this.state.room.send(CatchphraseServerInMessages.ConfirmGameConfig, {});
    }

    signalUpdateGameConfig = (patch) => {
        if (this.state.isMirror) return;
        audio.playSfx(SFX.BUTTON_PRESS);
        this.state.room?.send(CatchphraseServerInMessages.UpdateGameConfig, patch);
    }

    signalBackToMenu = async () => {
        if (this.state.isMirror) return;
        audio.stopAllVo();
        audio.playSfx(SFX.BUTTON_PRESS);
        this.setState({ showGameConfig: false });
        await this.sleep(500);
        this.state.room.send(CatchphraseServerInMessages.BackToMenu, {});
    }

    signalNewGame = async () => {
        if (this.state.isMirror) return;
        audio.playSfx(SFX.BUTTON_PRESS);
        audio.stopAllVo();
        audio.stopMusic({ fadeMs: 1000 });
        this.setState({ showPlayAgainButtons: false, showWinnersSection: false, });
        await this.sleep(500);
        this.state.room.send(CatchphraseServerInMessages.PlayAgain, {});
    }

    signalFinishTeamsSelection = async () => {
        if (this.state.isMirror) return;
        audio.playSfx(SFX.BUTTON_PRESS);
        audio.stopAllVo();
        this.setState({ showTeamsSection: false, });
        this.state.room.send(CatchphraseServerInMessages.FinishTeamsSelection, {});
        await this.sleep(500);
        this.setState({ showRoundIntro: true });
    }

    getRedirectURL(display = false) {
        const lobbyUrl = this.getLobbyUrl();
        if (display) {
            return process.env.REACT_APP_GAME_CITY_URL_DISPLAY || lobbyUrl;
        }
        return lobbyUrl || process.env.REACT_APP_GAME_CITY_URL;
    }

    /** Get HTTP URL for API calls from WebSocket URL */
    getServerHttpUrl = () => {
        const wsUrl = process.env.REACT_APP_GAME_SERVER_URL || '';
        return wsUrl.replace('ws://', 'http://').replace('wss://', 'https://');
    };

    /** Get room code from URL query param (when redirected from GameCityRoom) */
    getRoomCodeFromURL = () => {
        return this.getQueryStringValue("code");
    };

    /** Get the lobby URL for return navigation */
    getLobbyUrl = () => {
        let url = localStorage.getItem('catchphraseLobbyUrl');
        if (!url) {
            url = process.env.REACT_APP_GAME_CITY_URL || null;
        }
        if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
            url = 'https://' + url;
        }
        return url;
    };

    /**
     * Build lobby redirect URL with platform-aware path and optional query params.
     */
    getLobbyRedirectUrl = ({ lobbyCode, hostToken } = {}) => {
        const lobbyUrl = this.getLobbyUrl();
        if (!lobbyUrl) return null;

        const effectivePlatform = this.state.roomState?.platform || this.state.platform;
        let platformPath = "/lobby";
        if (effectivePlatform === APP_TYPES.ROKU) platformPath = "/roku";
        else if (effectivePlatform === APP_TYPES.FIRE_TV) platformPath = "/firetv";

        let url = `${lobbyUrl}${platformPath}`;
        if (lobbyCode) {
            url += `?createRoom=${lobbyCode}`;
            if (hostToken) {
                url += `&hostToken=${encodeURIComponent(hostToken)}`;
            }
        }
        return url;
    };

    /** Redirect to lobby via setState (triggers render redirect) */
    goToLobby = ({ lobbyCode, hostToken } = {}) => {
        const redirectUrl = this.getLobbyRedirectUrl({ lobbyCode, hostToken });
        if (redirectUrl) {
            localStorage.removeItem('catchphraseLobbyUrl');
            localStorage.removeItem('catchphraseHostToken');
            this.setState({ redirect: redirectUrl });
        }
    };

    toggleSkipTutorial = (e) => {
        audio.playSfx(SFX.BUTTON_PRESS);
        if (this.state.showStartButtons) {
            this.setState({ tickedSkipTutorial: e.target.checked });
        }
    }

    /** Update reconnection token in URL
     * Note: Uses 'reconnect' param, NOT 'token' (which is reserved for JWT host verification)
     */
    updateToken(token) {
        try {
            const url = new URL(window.location.href);
            url.searchParams.set('reconnect', token);
            window.history.replaceState(null, '', url.toString());
        } catch (e) {
            console.warn(e);
        }
    }

    async doStartTutorial() {
        this.setState({ showTutorial: true, showStartButtons: false, gameBegun: true });
        this.sendIfPrimary("show_tutorial", {});
        await this.sleep(5000);
        this.setState({ showTutorial: false });
        this.sendIfPrimary(CatchphraseServerInMessages.GameStarted, {});
    }

    async doStartGameConfig() {
        audio.stopAllVo();
        audio.stopAllMusic();
        audio.playMusic(MUSIC.MENU);
        audio.playVo(VO.GAME_LENGTH_SELECT);
        // Fire the restore as early as possible. The server is already in GameConfig by
        // the time it sends StartGameConfig (which triggered this method), so the message
        // is accepted immediately and the schema sync arrives before the slide-in
        // animation finishes — no visible flash of the server defaults.
        this.restoreSavedConfig();
        this.setState({
            showGameConfig: true, showStartButtons: false, gameBegun: true,
            showWinnersSection: false, showPlayAgainButtons: false,
            showCatchphrase: false, showPlayers: false, showRoundIntro: false,
            showRoundSummary: false, showTeamsSection: false,
            showAnswer: false, hideVideo: true,
        });
        await this.sleep(500);
        if (this.state.roomState.catchphraseData.gameState === GameStates.GameConfig) {
            this.sendIfPrimary(CatchphraseServerInMessages.HostReady);
        }
    }

    // Persisted config — survives full page reload / new game session.
    SAVED_CONFIG_KEY = "catchphrase_lastConfig";

    restoreSavedConfig() {
        if (this.state.isMirror) return;
        try {
            const raw = localStorage.getItem(this.SAVED_CONFIG_KEY);
            if (!raw) return;
            const saved = JSON.parse(raw);
            const patch = {};
            if (saved?.gameType === "quick" || saved?.gameType === "long") patch.gameType = saved.gameType;
            if (saved?.gameMode === "teams" || saved?.gameMode === "individuals") patch.gameMode = saved.gameMode;
            // Silent-migrate the retired "fuzzyHints" mode → "fuzzy" so returning users don't
            // get stuck on a value the server now rejects.
            if (saved?.answerMode === "fuzzy" || saved?.answerMode === "exact") patch.answerMode = saved.answerMode;
            else if (saved?.answerMode === "fuzzyHints") patch.answerMode = "fuzzy";
            if (Object.keys(patch).length === 0) return;
            console.log("[Host] Restoring saved config:", patch);
            this.sendIfPrimary(CatchphraseServerInMessages.UpdateGameConfig, patch);
        } catch (e) {
            console.warn("[Host] Failed to restore saved config:", e);
        }
    }

    saveCurrentConfig() {
        if (this.state.isMirror) return;
        try {
            const data = this.state.roomState?.catchphraseData;
            if (!data) return;
            const config = {
                gameType: data.gameType,
                gameMode: data.gameMode,
                answerMode: data.answerMode,
            };
            localStorage.setItem(this.SAVED_CONFIG_KEY, JSON.stringify(config));
            console.log("[Host] Saved config for next session:", config);
        } catch (e) {
            console.warn("[Host] Failed to save config:", e);
        }
    }

    async doStartTeamsSelection() {
        audio.stopAllVo();
        audio.playVo(VO.TEAM_SELECT);
        this.setState({ showTeamsSection: true, showGameConfig: false });
        this.saveCurrentConfig();
    }

    async doStartPhrase() {
        console.log("doStartPhrase");
        this._gridPhraseActive = false;
        const catchphraseData = this.state.roomState.catchphraseData;
        if (catchphraseData.round === 1) {
            audio.stopMusic({ fadeMs: 1000 });
            audio.playMusic(MUSIC.ROUND_ONE, { fadeMs: 1000 });
        }
        else if (catchphraseData.round === 2) {
            audio.stopMusic({ fadeMs: 1000 });
            audio.playMusic(MUSIC.ROUND_TWO, { fadeMs: 1000 });
        }
        else if (catchphraseData.round === 3) {
            audio.stopMusic({ fadeMs: 1000 });
            audio.playMusic(MUSIC.ROUND_THREE, { fadeMs: 1000 });
        }
        this.setState({ showCatchphrase: true, showRoundIntro: false, hideVideo: true, videoLoading: true });
        await this.sleep(500);

        if (catchphraseData.round === 1 && catchphraseData.revealCount === 0) audio.playVo(VO.FIRST_PHRASE_INTRO);
        else audio.playVo(pickVariant(VO.NEXT_PHRASE_INTRO));
        await this.sleep(3000);

        audio.playVo(pickVariant(VO.PHRASE_QUIP));
        this.setState({ hideVideo: false });
        console.log("doStartPhrase done", this.getCurrentCatchphrase());
    }

    async doStartGridPhrase(message = {}) {
        // Set IMMEDIATELY before any awaits — prevents onVideoStart from sending StartPhraseTimer
        this._gridPhraseActive = true;
        this._gridVosDone = false;
        this._gridVideoReady = false;
        this._gridTimerSent = false;
        const tStart = Date.now();
        console.log(`[Grid][client] doStartGridPhrase entry ts=${tStart} message=`, message);
        this.setState({ hideVideo: true, videoLoading: true });
        await this.sleep(500);

        // Step 1: Play board intro VO; show video as soon as it's ready (even during intro)
        const introPromise = message.isFirstGridPhrase
            ? audio.playVo(VO.BONUS_BOARD_INTRO)
            : audio.playVo(VO.BONUS_BOARD_RETURN);

        // Show video immediately so it can load while intro plays
        this.setState({ hideVideo: false, videoLoading: false });
        // Watchdog: if the VO promise hangs (audio rejection / autoplay block), don't block forever
        await Promise.race([introPromise, this.sleep(8000)]);

        // Step 2: Fire pre-selection VO WITHOUT awaiting — it overlaps the cycling animation,
        // so the cycle can start as soon as the video is ready instead of after another 2-3s of VO.
        if (message.isLastGridPhrase) {
            audio.playVo(pickVariant(VO.PRE_SELECT_LAST));
        } else if (message.previouslyCorrect) {
            audio.playVo(pickVariant(VO.PRE_SELECT_TEAM));
        } else {
            audio.playVo(pickVariant(VO.PRE_SELECT_AUTO));
        }

        // Step 3: VOs done (intro awaited, pre-select fire-and-forget) — wait for video if needed, then start selection
        this._gridVosDone = true;
        console.log(`[Grid][client] VOs done ts=${Date.now()} elapsedSinceEntry=${Date.now() - tStart}ms videoReady=${this._gridVideoReady}`);

        if (this._gridVideoReady) {
            await this.sleep(500);
            this._sendStartPhraseTimer("doStartGridPhrase (video ready)");
        } else {
            // Video might already be loaded (same src from previous reveal — onVideoStart won't fire again).
            // Give it a brief moment, then start selection anyway.
            await this.sleep(500);
            if (!this._gridVideoReady) {
                this._gridVideoReady = true;
                this._sendStartPhraseTimer("doStartGridPhrase (video timeout)");
            }
        }
    }

    _sendStartPhraseTimer = (source) => {
        if (this._gridTimerSent) {
            console.log(`[Grid][client] StartPhraseTimer suppressed (already sent) source=${source} ts=${Date.now()}`);
            return;
        }
        this._gridTimerSent = true;
        console.log(`[Grid][client] StartPhraseTimer send source=${source} ts=${Date.now()}`);
        this.sendIfPrimary(CatchphraseServerInMessages.StartPhraseTimer);
    }

    _tryStartGridSelection = async () => {
        if (!this._gridVosDone || !this._gridVideoReady) return;
        await this.sleep(500);
        this._sendStartPhraseTimer("_tryStartGridSelection");
    }

    async doReavealAnswer(shouldShowAnswer, timesUp, allPassed) {
        if (shouldShowAnswer) this.setState({ showAnswer: true });
        if (timesUp) {
            const catchphraseData = this.state.roomState.catchphraseData;
            const allLockedOut = catchphraseData.isGridPhrase && catchphraseData.teams.filter(t => t.playerIds.length > 0).every(t => t.answered || t.lockedOut);
            audio.playSfx(SFX.TIMES_UP_BELL);
            await this.sleep(500);
            if (allLockedOut) {
                audio.playVo(VO.GRID_VIEWING_TIMES_UP);
            } else {
                audio.playVo(pickVariant(VO.ROUND_TIMES_UP));
            }
        }

        if (allPassed) {
            audio.playSfx(SFX.TIMES_UP_BELL);
            await this.sleep(500);
            audio.playVo(VO.ALL_PASSED);
        }

        await this.sleep(5000);
        this.setState({ showAnswer: false, hideVideo: true });
        this.sendIfPrimary(CatchphraseServerInMessages.FinishPhrase);
    }

    async doRoundIntro() {
        audio.stopAllVo();
        this.setState({ showTeamsSection: false, showGameConfig: false, showRoundIntro: true, showPlayers: true });
        this.saveCurrentConfig();
        const catchphraseData = this.state.roomState.catchphraseData;
        setTimeout(() => {
            if(catchphraseData.gameState === GameStates.RoundIntro)
            this.sendIfPrimary(CatchphraseServerInMessages.HostReady);
        }, 500);

        if (catchphraseData.round === 1) {
            audio.playVo(VO.ROUND_ONE_INTRO);
            await this.sleep(13000);
        }
        else if (catchphraseData.round === 2) {
            audio.playVo(VO.ROUND_TWO_INTRO);
            await this.sleep(12000);
        }
        else if (catchphraseData.round === 3) {
            audio.playVo(VO.ROUND_THREE_INTRO);
            await this.sleep(16000);
        }
        if (catchphraseData.introSkipped) return;
        this.setState({ showRoundIntro: false });
        this.sendIfPrimary(CatchphraseServerInMessages.FinishRoundIntro);
    }

    async doStartRoundSummary() {
        const catchphraseData = this.state.roomState.catchphraseData;
        this.setState({ showCatchphrase: false, showPlayers: false });
        audio.stopMusic({ fadeMs: 1000 });
        await this.sleep(500);
        audio.playSfx(SFX.ROUND_END);
        audio.playMusic(MUSIC.ROUND_SUMMARY);
        this.setState({ showRoundSummary: true });
        await this.sleep(3000);

        if (catchphraseData.round === 1) {
            audio.playVo(VO.ROUND_ONE_SUMMARY);
            await this.sleep(10000);
        }
        else if (catchphraseData.round === 2) {
            audio.playVo(VO.ROUND_TWO_SUMMARY);
            await this.sleep(10000);
        }
        else if (catchphraseData.round === 3) {
            audio.playVo(VO.ROUND_THREE_SUMMARY);
            await this.sleep(8000);
        }

        this.setState({ showRoundSummary: false });
        await this.sleep(200);

        if (catchphraseData.round < 3) {
            this.sendIfPrimary(CatchphraseServerInMessages.FinishRoundSummary);
        } else {
            this.setState({ showRoundIntro: false, showWinnersSection: true });
            audio.playSfx(SFX.WINNERS);
            audio.stopMusic({ fadeMs: 500 });
            audio.playMusic(MUSIC.END_GAME);
            await this.sleep(2000);
            audio.playVo(VO.WINNERS);
            this.setState({ showPlayAgainButtons: true });
            this.sendIfPrimary(CatchphraseServerInMessages.ReachedEnd);
            await this.sleep(500);
            if (catchphraseData.gameState === GameStates.EndGame)
                this.sendIfPrimary(CatchphraseServerInMessages.HostReady);
        }
    }

    startLocationChecks() {
        this.state.room.send("location_check", { gameId, });
        this.locationCheckInterval = setInterval(() => {
            if (this.state.gotLocationPing) {
                console.log("Host connection is alive");
                this.setState({ gotLocationPing: false, connectionIssue: false, });
            } else {
                console.log("Host Connection Issue detected");
                this.setState({ connectionIssue: true, });
                LoggingService.streamLog(this.state.logStreamId, "Host Connection Issue Detected at Catchphrase");
            }
            this.state.room.send("location_check", { gameId, });
        }, 10000);
    }

    /**
     * Join the existing Catchphrase room as a mirror, retrying with backoff to
     * cover the race between the primary host's joinOrCreate and the mirror's
     * matchmaker join (both fired by the lobby redirect at the same instant).
     */
    joinAsMirrorWithRetry = async (roomCode, platform, maxAttempts = 10, delayMs = 1000) => {
        const mirrorOptions = { isHost: true, isMirror: true, roomCode, platform };
        let lastError = null;
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            try {
                const room = await this.client.join('catchphrase_room', mirrorOptions);
                this.setState({
                    room, roomId: room.state.roomCode, myId: room.sessionId,
                    connected: true, reconnectionToken: room.reconnectionToken,
                    showStartButtons: true, reconnectTries: 0,
                    // Set isMirror eagerly — we know we're a mirror because we just
                    // joined with isMirror:true. The server's mirror_status reply is
                    // only a confirmation; waiting for it leaves a window where an
                    // early change_game could route us through the primary host path.
                    isMirror: true,
                });
                this.updateToken(room.reconnectionToken);
                this.setupRoomListeners(room);
                return;
            } catch (e) {
                lastError = e;
                console.warn(`[Host] Mirror join attempt ${attempt}/${maxAttempts} failed:`, e?.message || e);
                if (attempt < maxAttempts) {
                    await this.sleep(delayMs);
                }
            }
        }
        console.error('[Host] Mirror join failed after retries:', lastError);
        this.goToLobby();
    };

    /** Create a new room directly (localhost mode or lobby redirect with code) */
    createRoom = () => {
        if (this.state.connected) return;

        // Mirror mode: join existing room as read-only secondary host
        const isMirrorHint = this.getQueryStringValue('mirror') === 'true';
        const mirrorCode = this.getQueryStringValue('code');
        if (isMirrorHint && mirrorCode) {
            console.log('[Host] Mirror mode detected, joining as mirror for:', mirrorCode);
            const platform = this.getQueryStringValue('platform') || 'web';
            this.joinAsMirrorWithRetry(mirrorCode, platform);
            return;
        }

        const roomCode = this.getQueryStringValue('code');
        const token = this.getQueryStringValue('token');      // JWT token from GameCity
        const lobbyUrl = this.getQueryStringValue('lobbyUrl'); // For return navigation
        const platform = this.getQueryStringValue('platform') || 'web';

        // Store lobby URL and host token for later return
        if (lobbyUrl) {
            localStorage.setItem('catchphraseLobbyUrl', decodeURIComponent(lobbyUrl));
        }
        if (token) {
            localStorage.setItem('catchphraseHostToken', token);
        }

        const options = { isHost: true, platform };
        if (roomCode) {
            options.roomCode = roomCode;
        }
        if (token) {
            options.token = token;
        }

        console.log('[Host] Creating room', roomCode ? `with code: ${roomCode}` : '(localhost mode)', 'platform:', platform);

        this.client.joinOrCreate('catchphrase_room', options).then(async (room) => {
            console.log(room.sessionId, "created/joined", room.name, "roomCode:", room.state.roomCode);
            this.setState({
                room: room,
                roomId: room.state.roomCode,
                myId: room.sessionId,
                connected: true,
                reconnectionToken: room.reconnectionToken,
                showStartButtons: true,
                reconnectTries: 0
            });
            audio.playVo(VO.INTRO);
            this.updateToken(room.reconnectionToken);

            // Signal host refresh to cancel any pending beacon closure timer
            if (roomCode) {
                const serverUrl = this.getServerHttpUrl();
                fetch(`${serverUrl}/api/host-refresh/${roomCode}`, { method: 'POST' }).catch(() => {});
            }

            this.setupRoomListeners(room);
        }).catch(async (e) => {
            console.error('Error creating room:', e);

            const isAuthError = e.message?.includes('auth') ||
                               e.message?.includes('token') ||
                               e.message?.includes('unauthorized') ||
                               e.code === 4001 || e.code === 4003;

            const gameServerUrl = process.env.REACT_APP_GAME_SERVER_URL || '';
            const isLocalhost = gameServerUrl.includes('localhost') || gameServerUrl.includes('127.0.0.1');

            if (isAuthError && !isLocalhost) {
                console.log('[Host] Auth failed, redirecting to lobby');
                this.goToLobby();
                return;
            }

            const newTries = this.state.reconnectTries + 1;
            this.setState({ connected: false, reconnectTries: newTries, hasErrored: true, errorMessage: e.message });

            // 30 × 2s = 60s budget. Slow TVs (LG webOS especially) can take 20–40s to
            // boot Catchphrase and complete the WebSocket handshake; 5 attempts gave
            // ~10s, which wasn't enough and bounced the host to lobby on transient flaps.
            if (newTries >= 30) {
                console.log(`[Host] Server unreachable after ${newTries} attempts, redirecting to lobby`);
                this.setState({ reconnectTries: 0 });
                if (!isLocalhost) {
                    this.goToLobby();
                }
                return;
            }

            await this.sleep(2000);
            this.doReconnect();
        });
    };

    /** Setup room event listeners (shared between reconnect and create) */
    setupRoomListeners = (room) => {
        const $ = Colyseus.getStateCallbacks(room);

        room.onStateChange.once((roomState) => {
            console.log("this is the first room state!", roomState);
            if (roomState.host?.uniqueId) {
                const logStreamId = roomState.roomUniqueId || roomState.host.uniqueId;
                LoggingService.streamLog(logStreamId, `Host Connected to Catchphrase, Reconnection Token: ${room.reconnectionToken}`);
                this.setState({ logStreamId });
            }
            const stateUpdate = { roomState: roomState, roomId: roomState.roomCode };
            if (roomState.platform && roomState.platform !== 'web') {
                stateUpdate.platform = roomState.platform;
            }
            this.setState(stateUpdate);

            // Send HOST_LOADED immediately
            room.send("HOST_LOADED", { gameId });

            // Ask server to confirm mirror status
            room.send("check_mirror_status", {});

            this.startLocationChecks();
            if (roomState.catchphraseData) {
                console.log("room gameState:", roomState.catchphraseData.gameState);
            }

            $(room.state).catchphraseData?.listen("timer", (value) => {
                const teams = roomState.catchphraseData?.teams;
                const PlayersCanAnswer = teams?.some(t => !t.answered);

                if (value === 10 && PlayersCanAnswer) audio.playVo(pickVariant(VO.TIMES_NEARLY_UP));
                if (value > 0 && value <= 10) {
                    if (value % 2 === 0) audio.playSfx(SFX.TIK);
                    else audio.playSfx(SFX.TOK);
                }
            });

            if (roomState.catchphraseData?.gameState && roomState.catchphraseData.gameState !== GameStates.MainMenu) this.docatchUp();
        });

        room.onStateChange((state) => {
            console.log("onStateChange", state);
            const stateUpdate = { roomState: state };
            if (state.platform && state.platform !== 'web') {
                stateUpdate.platform = state.platform;
            }
            this.setState(stateUpdate);
        });

        room.onMessage("location_confirmed", (message) => {
            console.log("location_confirmed", "received on", room.name, message);
            this.setState({ gotLocationPing: true, });
        });

        room.onMessage("mirror_status", (message) => {
            console.log("[Host] mirror_status received:", message);
            if (message.isMirror) {
                this.setState({ isMirror: true });
                console.log("[Host] Confirmed as MIRROR display");
            }
        });

        room.onMessage("animate_potato", (message) => {
            console.log("animate_potato", "recived for room:", room.name, "message:", message);
            this.animatePotato(message.id, message.animation);
        });

        room.onMessage("begin_tutorial", async (message) => {
            console.log("begin_tutorial", "recived for room:", room.name, "message:", message);
            this.doStartTutorial();
        });

        room.onMessage("end_tutorial", (message) => {
            console.log("end_tutorial", "recived for room:", room.name, "message:", message);
            this.setState({ showTutorial: false, showPlayers: true, });
        });

        room.onMessage("clicked_begin_game", (message) => {
            this.setState({ showStartButtons: false, });
        });

        room.onMessage("begin_game", (message) => {
            console.log("begin_game", "recived for room:", room.name, "message:", message);
            const gameState = this.state.roomState.catchphraseData.GameState;
            console.log("game state... ", gameState);
            if ((gameState === GameStates.Loading || gameState === GameStates.Idle) && !this.state.gameBegun) {
                this.setState({ showTutorial: false, gameBegun: true, showStartButtons: false, });
                this.sendIfPrimary(CatchphraseServerInMessages.GameStarted, { isTeams: true });
            }
        });

        room.onMessage(CatchphraseServerOutMessages.StartGameConfig, () => {
            console.log("StartGameConfig", "received for room:", room.name);
            this.doStartGameConfig();
        });

        room.onMessage("restart_to_menu", () => {
            console.log("restart_to_menu", "received for room:", room.name);
            audio.stopAllVo();
            audio.stopAllMusic();
            audio.playMusic(MUSIC.MENU);
            this.setState({
                showStartButtons: true, gameBegun: false,
                showGameConfig: false, showPlayAgainButtons: false,
                showCatchphrase: false, showPlayers: false, showRoundIntro: false,
                showRoundSummary: false, showTeamsSection: false,
                showAnswer: false, hideVideo: true, showWinnersSection: false,
            });
        });

        room.onMessage(CatchphraseServerOutMessages.StartTeamsSelection, (message) => {
            console.log("selecting_teams", "recived for room:", room.name, "message:", message);
            this.doStartTeamsSelection();
        });

        room.onMessage(CatchphraseServerOutMessages.TeamSelected, (message) => {
            console.log("TeamSelected", "recived for room:", room.name, "message:", message);
            audio.playSfx(SFX.BUTTON_PRESS);
        });

        room.onMessage(CatchphraseServerOutMessages.StartRoundIntro, async (message) => {
            console.log("StartRound1Intro", "recived for room:", room.name, "message:", message);
            this.doRoundIntro();
        });

        room.onMessage(CatchphraseServerOutMessages.SkipRoundIntro, async (message) => {
            console.log("SkipIntro", "recived for room:", room.name, "message:", message);
            this.setState({ showRoundIntro: false });
            audio.stopAllVo();
            this.sendIfPrimary(CatchphraseServerInMessages.FinishRoundIntro);
        });

        room.onMessage(CatchphraseServerOutMessages.StartPhrase, async (message) => {
            console.log("StartPhrase", "recived for room:", room.name, "message:", message);
            this.doStartPhrase();
        });

        room.onMessage(CatchphraseServerOutMessages.TriggerHint, async (message) => {
            console.log("TriggerHint", "recived for room:", room.name, "message:", message);
            const PlayersCanAnswer = this.state.roomState.catchphraseData?.teams?.some(t => !t.answered);
            if (PlayersCanAnswer) audio.playVo(pickVariant(VO.MID_ROUND_QUIP));
        });

        room.onMessage(CatchphraseServerOutMessages.PlayerGuessed, async (message) => {
            console.log("PlayerGuessed", "recived for room:", room.name, "message:", message);
            if (message.wasCorrect) {
                audio.playSfx(SFX.CORRECT_ANSWER);
                await this.sleep(500);
                audio.playVo(pickVariant(VO.CORRECT));
            }
            else {
                audio.playSfx(SFX.INCORRECT_ANSWER);
                await this.sleep(500);
                audio.playVo(pickVariant(VO.INCORRECT));
            }
        });

        room.onMessage(CatchphraseServerOutMessages.BuzzerTimedOut, async (message) => {
            console.log("buzzer_timed_out", "recived for room:", room.name, "message:", message);
            audio.playSfx(SFX.INCORRECT_ANSWER);
            await this.sleep(500);
            audio.playVo(pickVariant(VO.TIMED_OUT));
        });

        room.onMessage(CatchphraseServerOutMessages.PlayerPassed, async (message) => {
            console.log("PlayerPassed", "recived for room:", room.name, "message:", message);
            audio.playSfx(SFX.PASS);
        });

        room.onMessage(CatchphraseServerOutMessages.RevealAnswer, async (message) => {
            console.log("RevealAnswer", "recived for room:", room.name, "message:", message);
            this.doReavealAnswer(message.shouldShowAnswer, message.timesUp, message.allPassed);
        });

        room.onMessage(CatchphraseServerOutMessages.StartGridPhrase, async (message) => {
            console.log("StartGridPhrase", "recived for room:", room.name, "message:", message);
            this.doStartGridPhrase(message);
        });

        room.onMessage(CatchphraseServerOutMessages.StartRoundSummary, async (message) => {
            console.log("StartRoundSummary", "recived for room:", room.name, "message:", message);
            this.doStartRoundSummary();
        });

        room.onMessage(CatchphraseServerOutMessages.PlayerBuzzedIn, async (message) => {
            console.log("PlayerBuzzedIn", "recived for room:", room.name, "message:", message);
            audio.stopAllVo();
            audio.playSfx(SFX.BUZZER);
            if (!this.state.roomState.catchphraseData?.isMoneyDrop) {
                await this.sleep(1500);
                audio.playVo(pickVariant(VO.BUZZ_IN_QUIP));
            }
        });

        room.onMessage(CatchphraseServerOutMessages.GridSegmentSelected, async (message) => {
            const tReceived = Date.now();
            console.log(`[Grid][client] GridSegmentSelected ts=${tReceived} message=`, message);
            audio.playSfx(SFX.SEGMENT_REVEAL);
            const { index } = message;
            const catchphraseData = this.state.roomState.catchphraseData;
            const canAnswer = !!catchphraseData.teams.find(t => t.isCorrect);
            const allRevealed = (catchphraseData.revealCount + 1) >= catchphraseData.currentGridReveal.length;

            // Flash animation: set once and let CSS @keyframes handle the pulsing.
            // CSS @keyframes is 0.4s × 3 = 1.2s, so this matches the visual duration without extra dead time.
            this.setState({ flashingSegment: index });
            await this.sleep(1200);
            this.setState({ flashingSegment: null });

            // Reveal segment + start answer timer immediately
            console.log(`[Grid][client] sending StartPhraseTimer for reveal ts=${Date.now()} elapsedSinceSelected=${Date.now() - tReceived}ms`);
            this.sendIfPrimary(CatchphraseServerInMessages.StartPhraseTimer);

            // Post-reveal VO plays over the answer timer (fire-and-forget)
            if (allRevealed) {
                audio.playVo(pickVariant(VO.POST_REVEAL_ALL_ANSWER));
            } else if (canAnswer) {
                audio.playVo(pickVariant(VO.POST_REVEAL_ANSWER));
            } else {
                audio.playVo(pickVariant(VO.POST_REVEAL_VIEWING));
            }
        });

        // Handle change_game from server (return to lobby flow)
        room.onMessage("change_game", (message) => {
            console.log("change_game", "received for room:", room.name, "message:", message);
            if (this.state.isMirror) {
                // Mirror redirects back to lobby with mirror param so it re-mirrors the next game
                const lobbyUrl = message?.lobbyUrl || localStorage.getItem('catchphraseLobbyUrl');
                const lobbyCode = message?.lobbyCode;
                if (lobbyUrl && lobbyCode) {
                    const effectivePlatform = message.platform || this.state.roomState?.platform || this.state.platform;
                    let platformPath = "/lobby";
                    if (effectivePlatform === APP_TYPES.ROKU) platformPath = "/roku";
                    else if (effectivePlatform === APP_TYPES.FIRE_TV) platformPath = "/firetv";
                    window.location = `${lobbyUrl}${platformPath}?mirror=${lobbyCode}`;
                }
                return;
            }
            const lobbyCode = message?.lobbyCode;
            const hostToken = message?.hostToken || localStorage.getItem('catchphraseHostToken');
            this.goToLobby({ lobbyCode, hostToken });
            if (this.locationCheckInterval) clearInterval(this.locationCheckInterval);
            this.state.room?.leave(false);
        });

        room.onMessage("selecting_teams", (message) => {
            console.log("selecting_teams", "recived for room:", room.name, "message:", message);
            this.setState({ showTeamsSection: true });
        });

        room.onError((code, message) => {
            console.log(this.client.id, "couldn't join", room.name);
            console.error(`WS Received: onError`, code, message);
        });

        room.onLeave(async (code) => {
            console.log(this.client.id, "left", room.name);
            if (this.locationCheckInterval) {
                clearInterval(this.locationCheckInterval);
                this.locationCheckInterval = null;
            }
            if (!this.state.redirect) {
                this.setState({ connected: false, reconnectTries: this.state.reconnectTries + 1 });
                await this.sleep(1000);
                this.doReconnect();
            }
        });

        setTimeout(() => this.sendIfPrimary(CatchphraseServerInMessages.HostReady), 500);
    };

    doReconnect = () => {
        const reconnectToken = this.getQueryStringValue("reconnect");
        const roomCode = this.getQueryStringValue("code");
        const lobbyUrl = this.getRedirectURL();

        const gameServerUrl = process.env.REACT_APP_GAME_SERVER_URL || '';
        const isLocalhost = gameServerUrl.includes('localhost') || gameServerUrl.includes('127.0.0.1');

        console.log("ReconnectToken:", reconnectToken, "| RoomCode:", roomCode, "| Localhost:", isLocalhost);

        // In localhost mode (no room code from lobby), always create a new room
        if (isLocalhost && !roomCode && !reconnectToken?.includes(':')) {
            console.log('Localhost mode - creating new room directly');
            this.createRoom();
            return;
        }

        // If we have a room code from URL (new session from GameCity redirect) - create room
        if (roomCode && !reconnectToken?.includes(':')) {
            console.log("Creating room with code from URL:", roomCode);
            this.createRoom();
            return;
        }

        // Try to reconnect with session token (contains :)
        if (this.state.reconnectTries < 30 && reconnectToken && reconnectToken.includes(':')) {
            console.log("Reconnecting to room:", reconnectToken);

            if (this.state.connected === false) {
                this.client
                    .reconnect(reconnectToken)
                    .then((room) => {
                        console.log(room.sessionId, "reconnected to", room.name, "roomCode:", room.state.roomCode);
                        this.setState({
                            room: room,
                            roomId: room.state.roomCode,
                            myId: room.sessionId,
                            connected: true,
                            reconnectionToken: room.reconnectionToken,
                            showStartButtons: true,
                            reconnectTries: 0
                        });
                        this.updateToken(room.reconnectionToken);

                        this.setupRoomListeners(room);
                    })
                    .catch(async (e) => {
                        console.log("RECONNECT ERROR", e);
                        this.setState({ reconnectTries: 0 });
                        // If we still have mirror hints in the URL, rejoin as a fresh mirror
                        // rather than dumping the user to the lobby.
                        const isMirrorHint = this.getQueryStringValue('mirror') === 'true';
                        const mirrorCode = this.getQueryStringValue('code');
                        if (isMirrorHint && mirrorCode) {
                            console.log("Reconnect failed for mirror, attempting fresh mirror join");
                            this.createRoom();
                            return;
                        }
                        console.log("Reconnect failed, redirecting to lobby");
                        if (isLocalhost) {
                            this.createRoom();
                        } else {
                            this.goToLobby();
                        }
                    });
            }
        } else if (this.state.reconnectTries >= 30) {
            console.log("Max reconnect attempts");
            this.setState({ reconnectTries: 0 });
            // Same mirror fallback as above
            const isMirrorHint = this.getQueryStringValue('mirror') === 'true';
            const mirrorCode = this.getQueryStringValue('code');
            if (isMirrorHint && mirrorCode) {
                console.log("Max reconnect attempts on mirror, attempting fresh mirror join");
                this.createRoom();
                return;
            }
            if (isLocalhost) {
                this.createRoom();
            } else {
                this.goToLobby();
            }
        } else {
            console.log("No valid token, redirecting to lobby");
            if (lobbyUrl && !isLocalhost) {
                this.goToLobby();
            } else if (isLocalhost) {
                this.createRoom();
            } else {
                console.error("No lobby URL configured and no valid token");
                this.setState({ hasErrored: true, errorMessage: "Please start Catchphrase from the Game City lobby." });
            }
        }
    };

    docatchUp() {
        console.log("catching up", this.state.roomState.catchphraseData.gameState);
        //set all show states to false
        this.setState({
            showStartButtons: false,
            showGameConfig: false,
            showTeamsSection: false,
            showRoundIntro: false,
            showCatchphrase: false,
            showAnswer: false,
            showRoundSummary: false,
            showWinnersSection: false,
            showPlayAgainButtons: false,
            showPlayers: false,
        });
        audio.stopAllMusic();
        audio.stopAllVo();

        //set the correct show state to true
        switch (this.state.roomState.catchphraseData?.gameState) {  //todo change each state to a doState function becuase currently this will break the game
            case GameStates.MainMenu:
                this.setState({ showStartButtons: true });
                audio.playMusic(MUSIC.MENU);
                break;
            case GameStates.GameConfig:
                this.setState({ showGameConfig: true, gameBegun: true });
                audio.playMusic(MUSIC.MENU);
                break;
            case GameStates.SelectingTeams:
                this.setState({ showTeamsSection: true, gameBegun: true });
                audio.playMusic(MUSIC.MENU);
                break;
            case GameStates.RoundIntro:
                this.setState({ showRoundIntro: true, gameBegun: true, showPlayers: true });
                this.doRoundIntro();
                break;
            case GameStates.Playing:
                this.setState({ showCatchphrase: true, gameBegun: true, showPlayers: true, hideVideo: false });
                audio.playMusic(MUSIC.ROUND_ONE);
                break;
            case GameStates.RevealAnswer:
                this.setState({ showCatchphrase: true, showAnswer: true, gameBegun: true, showPlayers: true, hideVideo: false });
                audio.playMusic(MUSIC.ROUND_ONE);
                break;
            case GameStates.RoundSummary:
                this.setState({ showRoundSummary: true, gameBegun: true });
                this.doStartRoundSummary();
                break;
            case GameStates.EndGame:
                this.setState({ showWinnersSection: true, showPlayAgainButtons: true, gameBegun: true });
                break;
            default:
                console.warn("catch up - invalid state");
                break;
        }
    }


    getTeamPlayers(team) {
        if (!team) return [];
        return team.playerIds.map((id) => { return this.state.roomState.players.get(id) });
    }

    getUnassignedPlayers(players) {
        return players.filter((p) => { return p.catchphraseData?.teamIndex === -1 });
    }

    validateTeams(players, teams) {
        const allPlayersInTeams = players.filter(p => p.connected).every(p => p.catchphraseData?.teamIndex > -1);
        const teamsWithPlayers = teams.filter(t => t.playerIds.length > 0);
        return allPlayersInTeams && teamsWithPlayers.length >= 2;
    }

    onVideoStart = async () => {
        const ts = Date.now();
        console.log(`[Grid][client] onVideoStart ts=${ts} gridPhraseActive=${this._gridPhraseActive} vosDone=${this._gridVosDone} videoReady=${this._gridVideoReady}`);
        this.setState({ videoLoading: false });
        if (this._gridPhraseActive) {
            // Grid phrase: show video as soon as it's ready (even during VOs)
            this.setState({ hideVideo: false });
            this._gridVideoReady = true;
            this._tryStartGridSelection();
            return;
        }
        // Non-grid: wait for the catchphraseScreen overlay to finish its 0.3s slide-away transition
        await this.sleep(400);
        this.sendIfPrimary(CatchphraseServerInMessages.StartPhraseTimer);
    }

    getCurrentCatchphrase() {
        const catchphraseData = this.state.roomState?.catchphraseData;
        if (!catchphraseData) return null;
        if (catchphraseData.isGridPhrase)
            return catchphraseData.currentGridCatchphrase;

        const revealCount = catchphraseData.revealCount;
        return catchphraseData.currentCatchphrases.at(revealCount);
    }

    formatTimerDisplay() {
        const totalSeconds = Math.ceil(this.state.roomState.catchphraseData.timer);
        const minuets = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${minuets}:${String(seconds).padStart(2, '0')}`;
    }

    //#region render functions
    renderTextDiv(text, additionalStyles) {
        return <>
            <div className={`${styles.text} ${additionalStyles}`}>{text}
                <div className={styles.face}>{text} </div>
            </div>
        </>
    }

    renderSpashBG() {
        //const showSplash = [GameStates.Loading, GameStates.MainMenu].includes(gamesState);
        const showSplash = !this.state.gameBegun || !this.state.connected;
        return <img src={BG} className={`${styles.menuBackground} ${showSplash && styles.show}`} alt="splash"/>
    }

    renderLogoSection() {
        //const centerLogo = [GameStates.Loading, GameStates.MainMenu].includes(gamesState);
        return (
            <div className={`${styles.logoSection} ${this.state.connected && (this.state.gameBegun ? styles.topLeft : styles.menu)}`}>
                <img src={logo} className={styles.logo} alt="logo" />
                {this.renderTextDiv("Connecting...", `${styles.connectingMessage} ${!this.state.connected && styles.show}`)}
            </div>
        )
    }

    renderLicences() {
        const showLicences = this.state.showStartButtons && this.state.connected;
        return (
            <div className={`${styles.licences} ${showLicences && styles.show}`}>
                {this.renderTextDiv("Catch Phrase was created by Pasetta Productions Inc and Steven Radosh. All Rights Reserved")}
                {this.renderTextDiv("Licensed by All3Media International Limited")}
                {this.renderTextDiv("Produced by STV Productions in association with All3Media International Limited together with the inclusion of logos for STV Productions and All3Media International Limited")}
                {this.renderTextDiv(`Catch Phrase Logo, Imagery and Mr Chips character © ${new Date().getFullYear()} All3Media International Limited and STV Productions Limited`)}
            </div>
        )
    }

    renderMainMenu() {
        //const showMainMenu = [GameStates.MainMenu].includes(gamesState) && this.state.connected;
        const showMainMenu = this.state.showStartButtons && this.state.connected;
        return (
            <div className={`${styles.startButtonSection} ${showMainMenu && styles.show}`}>
                <button className={`${styles.mainButton}`} onClick={this.signalStartGame} disabled={this.state.isMirror} tabIndex='-1'>
                    {this.renderTextDiv("Start Game")}
                </button>
                <button className={`${styles.mainButton} ${styles.smaller} ${styles.alt}`} onClick={this.signalGoToLobby} disabled={this.state.isMirror} tabIndex='-1'>
                    {this.renderTextDiv("Return To Lobby")}
                </button>
                {/*<div className={styles.toggle}>*/}
                {/*    <input className={styles.checkbox} type="checkbox" id="checkbox" name="checkbox" onChange={this.toggleSkipTutorial} />*/}
                {/*    <label htmlFor="checkbox" className={styles.text}>Skip Tutorial*/}
                {/*        <div className={styles.face}>Skip Tutorial</div>*/}
                {/*    </label>*/}
                {/*</div>*/}
            </div>
        )
    }

    onVideoError = (e) => {
        console.error("video error", e);
    }

    onProgress = (e) => {
        console.log("video progress", e);
    }

    renderStaticVideoPlayer(catchphraseData, catchphrase) {
        const showCatchphrase = this.state.showCatchphrase;
        const anyBuzzedIn = catchphraseData?.teams?.some(t => t.isBuzzedIn);
        const pauseOnBuzz = anyBuzzedIn && !catchphraseData?.isMoneyDrop;
        return (
            <div className={`${styles.staticVideoPlayer} ${showCatchphrase ? styles.show : ''}`}>
                <VideoPlayer
                    ref={this.videoPlayer}
                    video={catchphrase?.video_url_low_res}
                    show={showCatchphrase}
                    playing={!this.state.hideVideo && !pauseOnBuzz}
                    loop={true}
                    muted={true}
                    onStart={this.onVideoStart}
                    onError={(e) => console.error("video error", e)}
                    className={styles.reactPlayer}
                />
            </div>
        )
    }

    renderCatchphraseSection(catchphraseData, catchphrase, buzzedInPlayer, buzzedInTeam) {
        const showCatchphrase = this.state.showCatchphrase;
        const showAnswer = this.state.showAnswer;
        const round = catchphraseData.round;
        const roundText = round === 1 ? "FIRST ROUND" : round === 2 ? "SECOND ROUND" : "FINAL ROUND";
        return (
            <div className={`${styles.catchphraseSection} ${showCatchphrase && styles.show}`} >
                <div className={`${styles.timer} ${catchphraseData.timerRunning && showCatchphrase && styles.show} ${catchphraseData.timerPaused && styles.paused}`} >{this.formatTimerDisplay()}</div>
                <div className={`${styles.border} ${buzzedInPlayer && styles.buzzedIn}`} style={{ "--team-colour": buzzedInTeam?.colour ?? "#d0d0e0" }} >
                    <div className={`${styles.roundDisplay}`}>{this.renderTextDiv(roundText)}</div>
                    <div className={`${styles.videoPlayer}`} >
                        {/* VideoPlayer is now rendered separately via renderStaticVideoPlayer */}
                        <div className={`${styles.pauseOverlay} ${buzzedInPlayer && !catchphraseData?.isMoneyDrop ? styles.show : ''}`}>
                            <svg viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" /></svg>
                        </div>
                        <div className={`${styles.gridReveal} ${catchphraseData?.gridSize === 3 ? styles.grid3x3 : ''}`} >
                            {
                                catchphraseData?.currentGridReveal.map((reveal, index) => {
                                    const highlighted = catchphraseData?.highlightedSegment === index;
                                    const flashing = this.state.flashingSegment === index;
                                    return <div key={`bonus-board-${index}`} className={`${styles.reveal} ${(reveal || !catchphraseData?.isGridPhrase) && styles.hide} ${highlighted && styles.highlighted} ${flashing && styles.flashing}`} >
                                        <img src={BonusBoard} alt="bonus board segment" />
                                    </div>
                                })
                            }
                        </div>
                        <img src={CatchphraseScreen} className={`${styles.catchphraseScreen} ${(this.state.videoLoading || this.state.hideVideo) && styles.show}`} alt="catchphrase" />
                        <div className={`${styles.answer} ${showAnswer && styles.show}`}>{catchphrase?.answers[0]}</div>
                        {/* Letter-shape hint moved to the client (see GameConfigEditor / LetterSlotInput);
                            no skeleton overlay on the host any more. */}
                    </div>
                </div>
            </div>
        )
    }

    renderPlayerSection(catchphraseData, teams, buzzedInPlayer, buzzedInTeams) {
        //const showPlayers = [GameStates.Playing, GameStates.RevealAnswer].includes(gamesState);
        const showPlayers = this.state.showPlayers;
        return (
            <div className={`${styles.playersSection} ${showPlayers && styles.show}`} >
                {
                    teams.filter(t => t.playerIds.length).map((team) => {
                        return <div className={`${styles.team} ${team.isBuzzedIn && styles.buzzedIn}`} key={team.id} style={{ "--team-colour": team.colour }}>
                            <div className={styles.podium} >
                                <div className={styles.score}>{team.score}
                                    <div className={styles.face}>{team.score}</div>
                                </div>
                            </div>
                            {
                                this.getTeamPlayers(team).filter(p => p).map((player) => {
                                    const lockedOut = this.state.buzzingIn || team?.lockedOut || team?.answered;
                                    return <div key={player.id} className={styles.player} >
                                        <Player player={player} team={team} lockedOut={lockedOut} room={this.state.room} />
                                    </div>
                                })
                            }
                        </div>
                    })
                }
            </div>
        )
    }

    renderPlayAgainSection() {
        //const showPlayAgainSection = [GameStates.EndGame].includes(gamesState);
        const showPlayAgainSection = this.state.showPlayAgainButtons;
        return (
            <div className={`${styles.playAgainButtonSection} ${showPlayAgainSection && styles.show}`}>
                <button className={`${styles.mainButton}`} onClick={this.signalNewGame} tabIndex='-1'>
                    {this.renderTextDiv("Play Again")}
                </button>
                <button className={`${styles.mainButton} ${styles.alt}`} onClick={this.signalGoToLobby} tabIndex='-1'>
                    {this.renderTextDiv("Return to lobby")}
                </button>
            </div>
        )
    }

    render() {
        //#region Handle Redirect
        if (this.state.redirect && !(process.env.NODE_ENV === "development" && this.state.hasErrored)) {
            return (
                <React.Fragment>
                    <div id="gameContainer" className={styles.gameContainer}>
                        <div className={styles.loadingContainer}>
                            <Loading loadingText={"Sending you to the lobby!"} />
                        </div>
                    </div>
                    <div style={{ opacity: 0 }}>
                        <Route path="/" render={() => (window.location = this.state.redirect)} />
                    </div>'
                </React.Fragment>
            )
        }
        //#endregion

        const players = this.state.roomState?.players ? Array.from(this.state.roomState.players.values()) : null;
        const catchphrase = this.getCurrentCatchphrase();
        const catchphraseData = this.state.roomState.catchphraseData;
        const teams = catchphraseData?.teams;
        const activeTeams = catchphraseData?.teams.filter(team => team.playerIds.length > 0);
        const buzzedInPlayer = players?.find(p => p.catchphraseData?.isBuzzedIn);
        const buzzedInTeam = activeTeams?.find(t => t.isBuzzedIn);
        const buzzedInTeams = activeTeams?.filter(t => t.isBuzzedIn);
        const showMoneyDrop = (this.state.showCatchphrase || this.state.showRoundIntro) && catchphraseData.isMoneyDrop;

        console.log("*** Players : ", players);
        return (
            <>
                {/*{ this.state.gameBegun &&*/}
                {/*    <div className={styles.videoPreLoad} >*/}
                {/*        <ReactPlayer*/}
                {/*            url={"https://gamecity.blob.core.windows.net/static-assets/catchprase/videos/Catch%20Phrase%20Animations%20A%20Bridge%20Too%20Far%20F5822_1.mp4"}*/}
                {/*            className={styles.reactPlayer} width='100%' height='100%' loop={true} playing={true} muted={true}*/}
                {/*        />*/}
                {/*    </div>*/}
                {/*}*/}
                <div id="gameContainer" className={`${styles.gameContainer} ${this.state.gameBegun && styles.showBgImage}`}>
                    <img src={studioBG} className={`${styles.mainBackground} ${this.state.gameBegun && styles.show}`} alt="background" />
                    {this.renderSpashBG()}
                    {this.renderLogoSection()}
                    {this.renderMainMenu()}
                    {this.renderLicences()}
                    {
                        this.state.connected && catchphraseData &&
                        <>
                            <Menu room={this.state.room} toggleMenu={this.toggleMenu} menuOpen={this.state.menuOpen}
                                mutedMusic={catchphraseData?.muteMusic} mutedSfx={catchphraseData?.muteSfx} gameState={catchphraseData?.gameState}
                                isMirror={this.state.isMirror} />

                            <RoomDetails styles={styles} url={this.getRedirectURL(false)} displayUrl={this.getRedirectURL(true)}
                                muted={catchphraseData?.muteMusic && catchphraseData?.muteSfx} roomId={this.state.roomId}
                                fullscreenAvailable={fullscreenAvailable} onToggleMute={this.toggleMute} onToggleFullScreen={this.toggleFullScreen} onToggleMenu={this.toggleMenu}
                                isMirror={this.state.isMirror}
                                hideButtons={this.state.isMirror || [APP_TYPES.ROKU, APP_TYPES.FIRE_TV].includes(this.state.roomState?.platform)} />

                            <GameConfigView
                                styles={styles}
                                show={this.state.showGameConfig}
                                catchphraseData={catchphraseData}
                                players={players}
                                onConfirm={this.signalConfirmGameConfig}
                                onBack={this.signalBackToMenu}
                                onUpdate={this.signalUpdateGameConfig}
                                isMirror={this.state.isMirror} />

                            <TeamsSelection styles={styles} show={this.state.showTeamsSection} players={players} unassignedPlayers={this.getUnassignedPlayers(players)} teams={teams}
                                timerRunning={catchphraseData?.timerRunning} timerText={this.formatTimerDisplay()} showConfirmButton={this.validateTeams(players, teams)} onFinishTeams={this.signalFinishTeamsSelection}
                                isMirror={this.state.isMirror} />

                            <RoundIntro styles={styles} show={this.state.showRoundIntro} round={catchphraseData?.round} />
                            {this.renderStaticVideoPlayer(catchphraseData, catchphrase)}
                            {this.renderCatchphraseSection(catchphraseData, catchphrase, buzzedInPlayer, buzzedInTeam)}

                            <MoneyDrop styles={styles} show={showMoneyDrop} teams={teams} moneyDropAmount={catchphraseData.moneyDropAmount} moneyDropMax={catchphraseData.moneyDropMax} />

                            {this.renderPlayerSection(catchphraseData, teams, buzzedInPlayer, buzzedInTeams)}
                            
                            <RoundSummary styles={styles} show={this.state.showRoundSummary} round={catchphraseData?.round} activeTeams={activeTeams} teams={teams} players={players} />

                            <WinnerSection styles={styles} showWinners={this.state.showWinnersSection} showPlayAgain={this.state.showPlayAgainButtons} activeTeams={activeTeams} teams={teams} players={players} />

                            <PlayAgainSection styles={styles} show={this.state.showPlayAgainButtons} onNewGame={this.signalNewGame} onGoToLobby={this.signalGoToLobby} isMirror={this.state.isMirror} />
                        </>
                    }
                    {this.isPrimary && this.renderDebugButtons(catchphraseData)}
                </div>
            </>
        );
    }
    //#endregion

    //#region Debug Functions
    renderDebugButtons(catchphraseData) {
        if (!catchphraseData || window.location.hostname !== "localhost") return;
        return (
            <div style={{ position: "absolute", zIndex: 999, pointerEvents: 'initial', fontSize: "10px" }}>
                <button onClick={() => this.setState({ connected: !this.state.connected })}>connect</button>
                <button onClick={() => this.setState({ gameBegun: !this.state.gameBegun })}>gameBegun</button>
                <button disabled >|</button>
                <button onClick={() => this.setState({ showStartButtons: !this.state.showStartButtons })}>showStartButtons</button>
                <button onClick={() => this.setState({ showGameConfig: !this.state.showGameConfig })}>showGameConfig</button>
                <button onClick={() => this.setState({ showTeamsSection: !this.state.showTeamsSection })}>showTeamsSection</button>
                <button onClick={() => this.setState({ showRoundIntro: !this.state.showRoundIntro })}>showRoundIntro</button>
                <button onClick={() => this.setState({ showCatchphrase: !this.state.showCatchphrase })}>showCatchphrase</button>
                <button onClick={() => this.setState({ showPlayers: !this.state.showPlayers })}>showPlayers</button>
                <button onClick={() => this.setState({ showRoundSummary: !this.state.showRoundSummary })}>showRoundSummary</button>
                <button onClick={() => this.setState({ showWinnersSection: !this.state.showWinnersSection })}>showWinnersSection</button>
                <button onClick={() => this.setState({ showPlayAgainButtons: !this.state.showPlayAgainButtons })}>showPlayAgainButtons</button>

                {/*<button onClick={() => this.testingSetCatchphraseDataState({ gameState: GameStates.Loading })}>Loading</button>*/}
                {/*<button onClick={() => this.testingSetCatchphraseDataState({ gameState: GameStates.MainMenu })}>MainMenu</button>*/}
                {/*<button onClick={() => this.testingSetCatchphraseDataState({ gameState: GameStates.Tutorial })}>Tutorial</button>*/}
                {/*<button onClick={() => this.testingSetCatchphraseDataState({ gameState: GameStates.GameSelect })}>GameSelect</button>*/}
                {/*<button onClick={() => this.testingSetCatchphraseDataState({ gameState: GameStates.RoundIntro })}>RoundIntro</button>*/}
                {/*<button onClick={() => this.testingSetCatchphraseDataState({ gameState: GameStates.SelectingTeams })}>SelectingTeams</button>*/}
                {/*<button onClick={() => this.testingSetCatchphraseDataState({ gameState: GameStates.Playing })}>Playing</button>*/}
                {/*<button onClick={() => this.testingSetCatchphraseDataState({ gameState: GameStates.RevealAnswer })}>RevealAnswer</button>*/}
                {/*<button onClick={() => this.testingSetCatchphraseDataState({ gameState: GameStates.RoundSummary })}>RoundSummary</button>*/}
                {/*<button onClick={() => this.testingSetCatchphraseDataState({ gameState: GameStates.EndGame })}>EndGame</button>*/}

                <button disabled >|</button>
                <button onClick={() => this.setState({ showAnswer: !this.state.showAnswer })}>showAnswer ({this.state.showAnswer ? "T" : "F"})</button>
                <button onClick={() => this.setState({ hideVideo: !this.state.hideVideo })}>hideVideo ({this.state.hideVideo ? "T" : "F"})</button>
                <button onClick={() => this.testingSetCatchphraseDataState({ isGridPhrase: !catchphraseData.isGridPhrase })}>setIsGridPhrase ({catchphraseData.isGridPhrase ? "T" : "F"})</button>
                <button onClick={() => this.testingSetCatchphraseDataState({ currentGridReveal: [!catchphraseData.currentGridReveal[0], false, false, false] })}>RevealGrid ({catchphraseData.currentGridReveal[0] ? "T" : "F"})</button>
                <button onClick={() => this.testingSetCatchphraseDataState({ isMoneyDrop: !catchphraseData.isMoneyDrop })}>setIsMoneyDrop ({catchphraseData.isMoneyDrop ? "T" : "F"})</button>
                <button onClick={() => this.testingSetCatchphraseDataState({ round: ((catchphraseData.round) % 3) + 1 })}>changeRound({catchphraseData.round})</button>
                <button onClick={() => this.testingSetCatchphraseDataState({ timerRunning: !catchphraseData.timerRunning })}>showTimer ({catchphraseData.timerRunning ? "T" : "F"})</button>
                <button onClick={() => this.testingBuzzIn()}>buzzIn ({this.state.roomState.catchphraseData.teams[0]?.isBuzzedIn ? "T" : "F"})</button>
                <button onClick={() => this.testingPass()}>pass ({this.state.roomState.players.values().toArray()[0]?.passed ? "T" : "F"})</button>
            </div>
        )
    }

    testingSetCatchphraseDataState(state) {
        this.setState({ roomState: { ...this.state.roomState, catchphraseData: { ...this.state.roomState.catchphraseData, ...state } } });
    }

    testingBuzzIn() {
        const team = this.state.roomState.catchphraseData.teams.at(0);
        team.isBuzzedIn = !team.isBuzzedIn;
        const player = this.state.roomState.players.values().toArray()[0];
        player.catchphraseData.isBuzzedIn = !player.catchphraseData.isBuzzedIn;
        this.setState({ roomState: { ...this.state.roomState } });
    }

    testingPass() {
        const player = this.state.roomState.players.values().toArray()[0];
        player.catchphraseData.passed = !player.catchphraseData.passed;
        this.setState({ roomState: { ...this.state.roomState } });
    }
    //#endregion
}